// 組織與場域 › 戶的詳情（SPEC #org-tree-ui-home-delete）：結構樹不在選取模式時點一戶 → 右側換成這一戶。
//   名稱 + 葉名稱、路徑（組織名起算到所在節點）、N 台設備（結構總數）、未指派標記；沒有分頁。
//   「家戶成員」卡（vms#33，HomeMembersCard）：列成員與角色，owner / admin 能加入、改角色、移出。
//   最下方「維護」區〔刪除這個<葉名稱>〕：只給組織 owner / org_admin 或這一戶的管理人（家戶 owner），其他人不畫（UX-11）。
//   第 3 級確認（UX-03）：設備會移除並回原廠、錄影與事件刪除、成員收通知、長期保管匯出包不退費（#vms-web-export-archive）、輸入戶名；
//   DELETE /v1/orgs/{org}/homes/{home}，REAUTH_REQUIRED 由 SDK 交給 StepUpHost 重新驗證後重送（AUTH-05）。
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useConfirm } from '../../components/ConfirmDialog';
import type { MyOrg } from '../../lib/planeApi';
import { treeApi, type OrgTreeItem } from '../../lib/orgTreeApi';
import { useTzResolver } from '../../lib/useTz';
import { archivesNoticeText, deleteHomeErrText, deviceWarnText } from './homeDelete';
import { HomeMembersCard } from './HomeMembersCard';

export function HomeDetail(props: {
  org: MyOrg;
  home: OrgTreeItem;
  leaf: string;
  /** 組織名起算到所在節點（含）的路徑字串 */
  pathText: string;
  showUnassigned: boolean;
  canDelete: boolean;
  onDeleted(name: string): void;
  onError(msg: string, gone: boolean): void;
}) {
  const { t } = useTranslation();
  const { org, home, leaf } = props;
  const name = home.name.trim();
  const devices = home.devices ?? home.subtree?.devices ?? 0;
  const [deleting, setDeleting] = useState(false);
  const { ask, dialog } = useConfirm();

  async function remove() {
    const ok = await ask({
      title: t('ot.home_delete_title', { name }),
      body: <DeleteBody org={org} homeId={home.home_id!} leaf={leaf} devices={devices} />,
      action: t('ot.home_delete_action', { leaf }),
      typeToConfirm: name,
    });
    if (!ok) return;
    setDeleting(true);
    try {
      await treeApi.deleteHome(org, home.home_id!);
      props.onDeleted(name);
    } catch (e) {
      const code = (e as { code?: string } | null)?.code;
      props.onError(deleteHomeErrText(t, leaf, e), code === 'HOME_NOT_FOUND');
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div data-testid="home-detail">
      <div className="row">
        <h3 className="ot-node-title">{name}</h3>
        <span className="ot-chip ot-leaf">{leaf}</span>
        {props.showUnassigned && home.management_status === 'unassigned' && <span className="badge badge-warn">{t('ot.unassigned')}</span>}
      </div>
      <p className="muted small" data-testid="home-summary">
        {props.pathText} · {t('ot.home_devices', { count: devices })}
      </p>
      {home.home_id && <HomeMembersCard org={org} homeId={home.home_id} homeName={name} />}
      {props.canDelete && (
        <div className="maint-zone" data-testid="home-maintenance">
          <h4>{t('ot.maint_title')}</h4>
          <div className="maint-row">
            <div>
              <strong>{t('ot.home_delete', { leaf })}</strong>
              <div className="muted small">{t('ot.home_delete_desc')}</div>
            </div>
            <button className="btn btn-sm btn-danger-outline" data-testid="home-delete" disabled={deleting} onClick={() => void remove()}>
              {deleting ? t('ot.home_deleting') : t('ot.home_delete', { leaf })}
            </button>
          </div>
        </div>
      )}
      {dialog}
    </div>
  );
}

/** 確認對話框內文：設備、錄影與事件、成員通知、長期保管匯出包（開對話框時才讀家戶詳細）。 */
function DeleteBody({ org, homeId, leaf, devices }: { org: MyOrg; homeId: string; leaf: string; devices: number }) {
  const { t } = useTranslation();
  const tzOf = useTzResolver();
  const q = useQuery({ queryKey: ['ot', org.org_id, 'home-archives', homeId], queryFn: () => treeApi.homeArchives(org, homeId), retry: false, staleTime: 0 });
  const archives = q.isError ? 'unknown' : q.isLoading ? undefined : (q.data ?? null);
  const arc = archivesNoticeText(t, leaf, archives, tzOf(homeId));
  return (
    <>
      <p data-testid="home-delete-warning">
        {deviceWarnText(t, leaf, devices)}
        {t('ot.home_delete_tail')}
      </p>
      {arc && <p data-testid="home-delete-archives">{arc}</p>}
    </>
  );
}
